"use client";

import {
    END_SEASON_MUTATION,
    FINALIZE_SEASON_TAGS_MUTATION,
    ISSUE_SEASON_AWARDS_MUTATION,
    START_NEW_SEASON_MUTATION,
} from "@/graphql/mutations/seasonManagementMutations";
import { SEASON_MANAGEMENT_STATUS_QUERY } from "@/graphql/queries/seasonManagementQueries";
import { useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { useState } from "react";
import {
    FiAlertTriangle,
    FiAward,
    FiCalendar,
    FiCheck,
    FiChevronDown,
    FiChevronUp,
    FiClock,
    FiFlag,
    FiGift,
    FiPlay,
    FiStar,
    FiUsers,
} from "react-icons/fi";

interface TopPlayerEntry {
    playerId: string;
    playerName: string;
    value: number;
    position: number;
}

interface ClanMVP {
    clanId: string;
    clanName: string;
    clanTag: string;
    clanColor: string;
    playerId: string;
    playerName: string;
    totalVp: number;
}

interface Season {
    id: string;
    name: string;
    startDate: string;
    endDate: string | null;
    infoEndDate: string | null;
    tagsFinalized: boolean;
    isActive: boolean;
}

interface SeasonManagementStatus {
    hasActiveSeason: boolean;
    currentSeason: Season | null;
    topStats: {
        topCheckins: TopPlayerEntry[];
        topRaidCheckins: TopPlayerEntry[];
        topWatchTime: TopPlayerEntry[];
    } | null;
    clanMvps: ClanMVP[] | null;
}

// Confirmation Modal Component
function ConfirmationModal({
    isOpen,
    title,
    message,
    confirmLabel,
    confirmVariant = "danger",
    onConfirm,
    onCancel,
    isLoading,
}: {
    isOpen: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    confirmVariant?: "danger" | "success";
    onConfirm: () => void;
    onCancel: () => void;
    isLoading: boolean;
}) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="mx-4 w-full max-w-md rounded-2xl border border-slate-300 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-900">
                <div className="mb-4 flex items-center gap-2">
                    <FiAlertTriangle className="h-6 w-6 text-amber-500" />
                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        {title}
                    </h3>
                </div>
                <p className="mb-6 text-slate-600 dark:text-slate-400">
                    {message}
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        disabled={isLoading}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={clsx(
                            "rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50",
                            confirmVariant === "danger"
                                ? "bg-red-600 hover:bg-red-700"
                                : "bg-green-600 hover:bg-green-700",
                        )}
                    >
                        {isLoading ? "Processing..." : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Top Stats Table Component
function TopStatsTable({
    title,
    icon: Icon,
    entries,
    unitLabel,
}: {
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    entries: TopPlayerEntry[];
    unitLabel: string;
}) {
    const formatValue = (value: number, label: string) => {
        if (label === "minutes") {
            const hours = Math.floor(value / 60);
            const mins = value % 60;
            return `${hours}h ${mins}m`;
        }
        return value.toLocaleString();
    };

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800">
            <div className="mb-3 flex items-center gap-2">
                <Icon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                <h4 className="font-semibold text-slate-900 dark:text-slate-100">
                    {title}
                </h4>
            </div>
            {entries.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    No data available
                </p>
            ) : (
                <div className="space-y-2">
                    {entries.map((entry, idx) => (
                        <div
                            key={idx}
                            className="flex items-center justify-between rounded-md bg-white p-2 dark:bg-slate-700"
                        >
                            <div className="flex items-center gap-2">
                                <span
                                    className={clsx(
                                        "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold",
                                        entry.position === 1 &&
                                            "bg-yellow-400 text-yellow-900 dark:bg-yellow-500 dark:text-yellow-900",
                                        entry.position === 2 &&
                                            "bg-slate-300 text-slate-700 dark:bg-slate-400 dark:text-slate-800",
                                        entry.position === 3 &&
                                            "bg-orange-400 text-orange-900 dark:bg-orange-500 dark:text-orange-900",
                                    )}
                                >
                                    {entry.position}
                                </span>
                                <span className="font-medium text-slate-900 dark:text-slate-100">
                                    {entry.playerName}
                                </span>
                            </div>
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                                {formatValue(entry.value, unitLabel)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function SeasonManagementPage() {
    const [showGuide, setShowGuide] = useState(true);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [newSeasonName, setNewSeasonName] = useState("");

    // Confirmation modals state
    const [showAwardsConfirm, setShowAwardsConfirm] = useState(false);
    const [showEndSeasonConfirm, setShowEndSeasonConfirm] = useState(false);
    const [showFinalizeTagsConfirm, setShowFinalizeTagsConfirm] =
        useState(false);
    const [showNewSeasonConfirm, setShowNewSeasonConfirm] = useState(false);

    // Dry run option
    const [isDryRun, setIsDryRun] = useState(false);

    // Fetch current season status
    const { data, loading, error, refetch } = useQuery(
        SEASON_MANAGEMENT_STATUS_QUERY,
        {
            fetchPolicy: "network-only",
        },
    );

    // Mutations
    const [issueAwards, { loading: issuingAwards }] = useMutation(
        ISSUE_SEASON_AWARDS_MUTATION,
        {
            onCompleted: (data) => {
                setSuccessMessage(data.issueSeasonAwards.message);
                setShowAwardsConfirm(false);
                refetch();
            },
            onError: (error) => {
                setErrorMessage(error.message);
                setShowAwardsConfirm(false);
            },
        },
    );

    const [endSeason, { loading: endingSeason }] = useMutation(
        END_SEASON_MUTATION,
        {
            onCompleted: (data) => {
                setSuccessMessage(data.endSeason.message);
                setShowEndSeasonConfirm(false);
                refetch();
            },
            onError: (error) => {
                setErrorMessage(error.message);
                setShowEndSeasonConfirm(false);
            },
        },
    );

    const [finalizeTags, { loading: finalizingTags }] = useMutation(
        FINALIZE_SEASON_TAGS_MUTATION,
        {
            onCompleted: (data) => {
                setSuccessMessage(data.finalizeSeasonTags.message);
                setShowFinalizeTagsConfirm(false);
                refetch();
            },
            onError: (error) => {
                setErrorMessage(error.message);
                setShowFinalizeTagsConfirm(false);
            },
        },
    );

    const [startNewSeason, { loading: startingNewSeason }] = useMutation(
        START_NEW_SEASON_MUTATION,
        {
            onCompleted: (data) => {
                setSuccessMessage(data.startNewSeason.message);
                setShowNewSeasonConfirm(false);
                setNewSeasonName("");
                refetch();
            },
            onError: (error) => {
                setErrorMessage(error.message);
                setShowNewSeasonConfirm(false);
            },
        },
    );

    const seasonStatus: SeasonManagementStatus | null =
        data?.seasonManagementStatus;

    const handleIssueAwards = () => {
        if (!seasonStatus?.topStats) return;

        const topCheckins = seasonStatus.topStats.topCheckins.map((e) => ({
            playerName: e.playerName,
            position: e.position,
        }));
        const topRaids = seasonStatus.topStats.topRaidCheckins.map((e) => ({
            playerName: e.playerName,
            position: e.position,
        }));
        const topWatchTime = seasonStatus.topStats.topWatchTime.map((e) => ({
            playerName: e.playerName,
            position: e.position,
        }));

        issueAwards({
            variables: {
                topCheckins,
                topRaids,
                topWatchTime,
                dryRun: isDryRun,
            },
        });
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-AU", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // Determine current step
    const getCurrentStep = (): number => {
        if (!seasonStatus?.currentSeason) return 4; // Ready for new season
        if (seasonStatus.currentSeason.isActive) return 1; // Active season - can view stats/issue awards
        if (!seasonStatus.currentSeason.tagsFinalized) return 2; // Season ended but tags not finalized
        return 3; // Tags finalized, can start new season
    };

    const currentStep = getCurrentStep();

    if (loading) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <div className="flex items-center justify-center p-8">
                        <div className="text-slate-600 dark:text-slate-400">
                            Loading season management...
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-red-300 bg-red-50 p-6 shadow-lg dark:border-red-700 dark:bg-red-900/20">
                    <div className="text-red-800 dark:text-red-400">
                        Error loading season management: {error.message}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto mt-4 w-full px-4">
            {/* TOP: End of Season Guide (Full Width, Minimizable) */}
            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <button
                    onClick={() => setShowGuide(!showGuide)}
                    className="flex w-full items-center justify-between"
                >
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        End of Season Guide
                    </h2>
                    {showGuide ? (
                        <FiChevronUp className="h-5 w-5 text-slate-500" />
                    ) : (
                        <FiChevronDown className="h-5 w-5 text-slate-500" />
                    )}
                </button>

                {showGuide && (
                    <div className="mt-4">
                        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                            This page helps you manage the end of season
                            process. Follow these steps in order to properly
                            close out a season and start a new one.
                        </p>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                            <div
                                className={clsx(
                                    "rounded-lg border p-4",
                                    currentStep === 1
                                        ? "border-brand bg-brand/5"
                                        : "border-slate-200 dark:border-slate-700",
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <span
                                        className={clsx(
                                            "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold",
                                            currentStep === 1
                                                ? "bg-brand text-white"
                                                : currentStep > 1
                                                  ? "bg-green-500 text-white"
                                                  : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400",
                                        )}
                                    >
                                        {currentStep > 1 ? "✓" : "1"}
                                    </span>
                                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                        Issue Awards
                                    </h4>
                                </div>
                                <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                                    Review top stats and award VP to top
                                    players. Use dry run first.
                                </p>
                            </div>

                            <div
                                className={clsx(
                                    "rounded-lg border p-4",
                                    currentStep === 1
                                        ? "border-brand bg-brand/5"
                                        : "border-slate-200 dark:border-slate-700",
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <span
                                        className={clsx(
                                            "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold",
                                            currentStep === 1
                                                ? "bg-brand text-white"
                                                : currentStep > 1
                                                  ? "bg-green-500 text-white"
                                                  : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400",
                                        )}
                                    >
                                        {currentStep > 1 ? "✓" : "1b"}
                                    </span>
                                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                        End Season
                                    </h4>
                                </div>
                                <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                                    Set the end date. Does NOT award
                                    end-of-season tags yet.
                                </p>
                            </div>

                            <div
                                className={clsx(
                                    "rounded-lg border p-4",
                                    currentStep === 2
                                        ? "border-brand bg-brand/5"
                                        : "border-slate-200 dark:border-slate-700",
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <span
                                        className={clsx(
                                            "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold",
                                            currentStep === 2
                                                ? "bg-brand text-white"
                                                : currentStep > 2
                                                  ? "bg-green-500 text-white"
                                                  : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400",
                                        )}
                                    >
                                        {currentStep > 2 ? "✓" : "2"}
                                    </span>
                                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                        Finalize Tags
                                    </h4>
                                </div>
                                <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                                    After announcing on stream, enable
                                    end-of-season tag awards.
                                </p>
                            </div>

                            <div
                                className={clsx(
                                    "rounded-lg border p-4",
                                    currentStep >= 3
                                        ? "border-brand bg-brand/5"
                                        : "border-slate-200 dark:border-slate-700",
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <span
                                        className={clsx(
                                            "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold",
                                            currentStep >= 3
                                                ? "bg-brand text-white"
                                                : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400",
                                        )}
                                    >
                                        3
                                    </span>
                                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                        Start New Season
                                    </h4>
                                </div>
                                <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                                    Create a fresh season for the community.
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                                <strong>Note:</strong> All actions require staff
                                permissions. End-of-season tags (Thor,
                                Vedrfolnir, Surtr, Nidhoggr, Fenrir) are only
                                awarded after finalizing.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Clan MVPs Section - Show when season has ended but tags not finalized */}
            {seasonStatus?.clanMvps && seasonStatus.clanMvps.length > 0 && (
                <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <div className="mb-4 flex items-center gap-2">
                        <FiAward className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                            Clan MVPs
                        </h2>
                        {!seasonStatus.currentSeason?.isActive &&
                            !seasonStatus.currentSeason?.tagsFinalized && (
                                <span className="ml-2 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                                    Awaiting Finalization
                                </span>
                            )}
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {seasonStatus.clanMvps.map((mvp) => (
                            <div
                                key={mvp.clanId}
                                className="rounded-lg border p-4"
                                style={{
                                    borderColor: mvp.clanColor,
                                    backgroundColor: `${mvp.clanColor}10`,
                                }}
                            >
                                <div className="mb-2 flex items-center gap-2">
                                    <span
                                        className="h-3 w-3 rounded-full"
                                        style={{
                                            backgroundColor: mvp.clanColor,
                                        }}
                                    />
                                    <span
                                        className="font-bold"
                                        style={{ color: mvp.clanColor }}
                                    >
                                        [{mvp.clanTag}] {mvp.clanName}
                                    </span>
                                </div>
                                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                    {mvp.playerName}
                                </div>
                                <div className="text-sm text-slate-600 dark:text-slate-400">
                                    {mvp.totalVp.toLocaleString()} VP
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Messages */}
            {successMessage && (
                <div className="mb-6 rounded-lg bg-green-100 p-3 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                    <pre className="text-sm font-medium whitespace-pre-wrap">
                        {successMessage}
                    </pre>
                </div>
            )}
            {errorMessage && (
                <div className="mb-6 rounded-lg bg-red-100 p-3 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                    <p className="text-sm font-medium">{errorMessage}</p>
                </div>
            )}

            {/* MAIN CONTENT: Two Column Layout - Controls Left, Stats Right */}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
                {/* LEFT COLUMN: Season Info + Issue Awards */}
                <div className="space-y-6 xl:col-span-1">
                    {/* Current Season Info */}
                    <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                        <div className="mb-4 flex items-center gap-2">
                            <FiCalendar className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                                Current Season
                            </h2>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Status
                                </span>
                                {seasonStatus?.currentSeason ? (
                                    <span
                                        className={clsx(
                                            "flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium",
                                            seasonStatus.currentSeason.isActive
                                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                                : seasonStatus.currentSeason
                                                        .tagsFinalized
                                                  ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                                                  : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
                                        )}
                                    >
                                        <span
                                            className={clsx(
                                                "h-2 w-2 rounded-full",
                                                seasonStatus.currentSeason
                                                    .isActive &&
                                                    "animate-pulse bg-green-500",
                                                !seasonStatus.currentSeason
                                                    .isActive &&
                                                    !seasonStatus.currentSeason
                                                        .tagsFinalized &&
                                                    "bg-amber-500",
                                                seasonStatus.currentSeason
                                                    .tagsFinalized &&
                                                    "bg-purple-500",
                                            )}
                                        />
                                        {seasonStatus.currentSeason.isActive
                                            ? "Active"
                                            : seasonStatus.currentSeason
                                                    .tagsFinalized
                                              ? "Finalized"
                                              : "Ended"}
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2 rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-400">
                                        <span className="h-2 w-2 rounded-full bg-slate-400" />
                                        No Season
                                    </span>
                                )}
                            </div>

                            {seasonStatus?.currentSeason && (
                                <div className="mt-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500 dark:text-slate-400">
                                            Name:
                                        </span>
                                        <span className="font-medium text-slate-900 dark:text-slate-100">
                                            {seasonStatus.currentSeason.name}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500 dark:text-slate-400">
                                            Started:
                                        </span>
                                        <span className="font-medium text-slate-900 dark:text-slate-100">
                                            {formatDate(
                                                seasonStatus.currentSeason
                                                    .startDate,
                                            )}
                                        </span>
                                    </div>
                                    {seasonStatus.currentSeason.endDate && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500 dark:text-slate-400">
                                                Ended:
                                            </span>
                                            <span className="font-medium text-slate-900 dark:text-slate-100">
                                                {formatDate(
                                                    seasonStatus.currentSeason
                                                        .endDate,
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500 dark:text-slate-400">
                                            Tags Finalized:
                                        </span>
                                        <span
                                            className={clsx(
                                                "font-medium",
                                                seasonStatus.currentSeason
                                                    .tagsFinalized
                                                    ? "text-green-600 dark:text-green-400"
                                                    : "text-slate-900 dark:text-slate-100",
                                            )}
                                        >
                                            {seasonStatus.currentSeason
                                                .tagsFinalized
                                                ? "Yes"
                                                : "No"}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Season Actions */}
                        <div className="mt-4 space-y-3">
                            {/* Step 1: End Season (only when active) */}
                            {currentStep === 1 && (
                                <button
                                    onClick={() =>
                                        setShowEndSeasonConfirm(true)
                                    }
                                    disabled={endingSeason}
                                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <FiFlag className="h-4 w-4" />
                                    {endingSeason ? "Ending..." : "End Season"}
                                </button>
                            )}

                            {/* Step 2: Finalize Tags (after season ended) */}
                            {currentStep === 2 && (
                                <>
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                                        <p className="text-xs text-amber-800 dark:text-amber-300">
                                            Season ended. Click below after
                                            announcing tags on stream.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() =>
                                            setShowFinalizeTagsConfirm(true)
                                        }
                                        disabled={finalizingTags}
                                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <FiGift className="h-4 w-4" />
                                        {finalizingTags
                                            ? "Finalizing..."
                                            : "Finalize Tags"}
                                    </button>
                                </>
                            )}

                            {/* Step 3: Start New Season */}
                            {(currentStep === 3 || currentStep === 4) && (
                                <>
                                    {currentStep === 3 && (
                                        <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
                                            <p className="text-xs text-green-800 dark:text-green-300">
                                                Tags finalized. Ready for new
                                                season.
                                            </p>
                                        </div>
                                    )}
                                    <input
                                        type="text"
                                        value={newSeasonName}
                                        onChange={(e) =>
                                            setNewSeasonName(e.target.value)
                                        }
                                        placeholder="e.g., Season 5"
                                        className="focus:border-brand focus:ring-brand/50 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:ring-2 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                                    />
                                    <button
                                        onClick={() =>
                                            setShowNewSeasonConfirm(true)
                                        }
                                        disabled={
                                            startingNewSeason ||
                                            !newSeasonName.trim()
                                        }
                                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <FiPlay className="h-4 w-4" />
                                        {startingNewSeason
                                            ? "Starting..."
                                            : "Start New Season"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Issue Awards */}
                    <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                        <div className="mb-4 flex items-center gap-2">
                            <FiAward className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                                Issue Awards
                            </h2>
                        </div>

                        {currentStep === 1 && seasonStatus?.topStats ? (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                    Award VP to top players. Position 1 gets
                                    10,000 VP, Position 2 gets 5,000 VP,
                                    Position 3 gets 3,000 VP.
                                </p>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="dryRun"
                                        checked={isDryRun}
                                        onChange={(e) =>
                                            setIsDryRun(e.target.checked)
                                        }
                                        className="text-brand focus:ring-brand h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                                    />
                                    <label
                                        htmlFor="dryRun"
                                        className="text-sm text-slate-700 dark:text-slate-300"
                                    >
                                        Dry Run (preview only)
                                    </label>
                                </div>

                                <button
                                    onClick={() => setShowAwardsConfirm(true)}
                                    disabled={issuingAwards}
                                    className={clsx(
                                        "flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                        isDryRun
                                            ? "bg-slate-600 hover:bg-slate-700"
                                            : "bg-brand hover:bg-brand/90",
                                    )}
                                >
                                    <FiAward className="h-4 w-4" />
                                    {issuingAwards
                                        ? "Processing..."
                                        : isDryRun
                                          ? "Preview Awards"
                                          : "Issue Awards"}
                                </button>
                            </div>
                        ) : currentStep > 1 ? (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800">
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                    Season has ended. Awards should have been
                                    issued before ending.
                                </p>
                            </div>
                        ) : (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800">
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                    No active season. Start a new season first.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMNS: Top Stats (3 columns side by side) */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg xl:col-span-3 dark:border-slate-700 dark:bg-slate-900">
                    <div className="mb-4 flex items-center gap-2">
                        <FiStar className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                            Top Stats
                        </h2>
                    </div>

                    {seasonStatus?.topStats ? (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                            <TopStatsTable
                                title="Checkins"
                                icon={FiCheck}
                                entries={seasonStatus.topStats.topCheckins}
                                unitLabel="checkins"
                            />
                            <TopStatsTable
                                title="Raid Checkins"
                                icon={FiUsers}
                                entries={seasonStatus.topStats.topRaidCheckins}
                                unitLabel="raids"
                            />
                            <TopStatsTable
                                title="Watch Time"
                                icon={FiClock}
                                entries={seasonStatus.topStats.topWatchTime}
                                unitLabel="minutes"
                            />
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            No active season to display stats.
                        </p>
                    )}
                </div>
            </div>

            {/* Confirmation Modals */}
            <ConfirmationModal
                isOpen={showAwardsConfirm}
                title={isDryRun ? "Preview Awards (Dry Run)" : "Issue Awards"}
                message={
                    isDryRun
                        ? "This will simulate the VP awards without making any changes. You can review the results and then issue for real."
                        : "This will award VP to all the top players shown above. This action cannot be undone. Are you sure?"
                }
                confirmLabel={isDryRun ? "Preview" : "Issue Awards"}
                confirmVariant={isDryRun ? "success" : "danger"}
                onConfirm={handleIssueAwards}
                onCancel={() => setShowAwardsConfirm(false)}
                isLoading={issuingAwards}
            />

            <ConfirmationModal
                isOpen={showEndSeasonConfirm}
                title="End Season"
                message={`This will end "${seasonStatus?.currentSeason?.name || "the current season"}". The season will no longer be active, but end-of-season tags will NOT be awarded until you finalize them. Continue?`}
                confirmLabel="End Season"
                confirmVariant="danger"
                onConfirm={() => endSeason()}
                onCancel={() => setShowEndSeasonConfirm(false)}
                isLoading={endingSeason}
            />

            <ConfirmationModal
                isOpen={showFinalizeTagsConfirm}
                title="Finalize Tags"
                message="This will enable the end-of-season tags (Thor, Vedrfolnir, Surtr, Nidhoggr, Fenrir) to be awarded. Only do this AFTER announcing them on stream. Continue?"
                confirmLabel="Finalize Tags"
                confirmVariant="success"
                onConfirm={() => finalizeTags()}
                onCancel={() => setShowFinalizeTagsConfirm(false)}
                isLoading={finalizingTags}
            />

            <ConfirmationModal
                isOpen={showNewSeasonConfirm}
                title="Start New Season"
                message={`This will create a new season called "${newSeasonName}". Continue?`}
                confirmLabel="Start Season"
                confirmVariant="success"
                onConfirm={() =>
                    startNewSeason({ variables: { name: newSeasonName } })
                }
                onCancel={() => setShowNewSeasonConfirm(false)}
                isLoading={startingNewSeason}
            />
        </div>
    );
}
